
import { gridConfig } from '@core/config/grid.config';
import { ArticleGroupedRibbons, Headlines, Data as ArticleData, ArticleExtraContent } from '@root/modules/article/types/article';
import articleConfig from '@root/modules/article/config/article.config';

import articleScroll from '@root/views/article/utils/articleScrollHandler';
import getArticleRibbons from '@root/views/article/utils/getArticleRibbons';
// Article info section
import ArticleInfoNormalTemplate from '@root/views/article/components/info/ArticleInfoNormalTemplate.vue';
import ArticleInfoFullTemplate from '@root/views/article/components/info/ArticleInfoFullTemplate.vue';
import ArticleInfoSmallTemplate from '@root/views/article/components/info/ArticleInfoSmallTemplate.vue';
import ArticleInfoMultimedijaTemplate from '@root/views/article/components/info/ArticleInfoMultimedijaTemplate.vue';
import ArticleAdultLayer from '@root/views/article/components/ArticleAdultLayer.vue';
import ArticleRibbons from '@root/views/article/components/ArticleRibbons.vue';

// Article body components
import ArticleBody from '@root/views/article/components/ArticleBody.vue';
import ArticleBodyMultipage from '@root/views/article/components/ArticleBodyMultipage.vue';
import ArticleHeadlinesSection from '@root/views/article/components/headlines/ArticleHeadlinesSection.vue';
import ArticleInfoSocial from '@root/views/article/components/info/ArticleInfoSocial.vue';
import AdFormBanner from '@ads/components/adForm/AdFormBanner.vue';
import getBannerKey from '@root/modules/ads/utils/getBannerKey';

interface Data {
  isAdultConfirmed: boolean;
  isMobileView: boolean;
  relatedArticlesSettings: {
    minParagraphsLength: number;
    afterParagraphPosition: number;
    articlesLimit: number;
    articlesInBodyLimit: number;
    endOfArticleBody: boolean;
    disableRelatedArticles: boolean;
  };
  fontSize: number;
}

interface Methods {
  confirmAdult: () => void;
  handleSetFontSize: () => void;
  getKey: (params: Record<string, any>) => string;
}

interface Computed {
  isCrArticle: boolean;
  topSection: string;
  topFragmentsNumber: number;
  isAdultArticle: boolean;
  isMultipage: boolean;
  contentCenterClass: Record<string, boolean>;
  articleRibbons: ArticleGroupedRibbons | null;
  disableAds: boolean;
  disabledAdsList: string[];
  adPlacements: Record<string, any>;
  contentAdPlacement: string;
  contentFallbackAdPlacement: string;
  contentFirstAdPlacement: Record<string, any>;
  isMultimedijaArticle: boolean;
}

interface Props {
  article: ArticleData;
  headlines: Headlines | [];
  isArticleFetching: boolean;
}

const topSectionMapping: Record<string, string> = {
  default: 'ArticleInfoNormalTemplate',
  fullImage: 'ArticleInfoFullTemplate',
  smallImage: 'ArticleInfoSmallTemplate',
  wideImage: 'ArticleInfoNormalTemplate',
  multimedija: 'ArticleInfoMultimedijaTemplate',
};

export default articleScroll.extend<Data, Methods, Computed, Props>({
  components: {
    ArticleInfoNormalTemplate,
    ArticleInfoFullTemplate,
    ArticleInfoSmallTemplate,
    ArticleInfoMultimedijaTemplate,
    ArticleBody,
    ArticleBodyMultipage,
    ArticleHeadlinesSection,
    ArticleAdultLayer,
    ArticleRibbons,
    ArticleInfoSocial,
    AdFormBanner,
  },
  props: {
    article: {
      type: Object,
      required: true,
    },
    headlines: {
      type: Array,
      required: true,
    },
    isArticleFetching: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      isAdultConfirmed: false,
      isMobileView: false,
      relatedArticlesSettings: this.$channelConfig('page').article.component.relatedArticles,
      fontSize: 1,
    };
  },
  computed: {
    isCrArticle() {
      const articles = this.$store.getters['article/getCrArticles'];

      return articles.find((article: number) => article === this.article.id);
    },
    topSection() {
      const template = this.$channelConfig('page').article.component.info.template || this.article.settings.articleTemplate;
      return topSectionMapping[template] || 'ArticleInfoNormalTemplate';
    },
    topFragmentsNumber() {
      const leadPosition = this.$channelConfig('page').article.component.info.leadPosition;
      return leadPosition === 'top' || this.topSection === 'ArticleInfoSmallTemplate' ? articleConfig.ads.first_position : 0;
    },
    isAdultArticle() {
      return this.article.settings['icon.adult'] || this.article.settings['articleAdultContent'] || false;
    },
    isMultipage() {
      return this.article.settings.isMultiPage || false;
    },
    contentCenterClass() {
      return { 'C-content-center': this.article.banners.disableAds };
    },
    articleRibbons() {
      const articleExtraContent: ArticleExtraContent = this.$store.getters['article/getArticleExtraContent'];
      if (!(articleExtraContent && articleExtraContent.ribbons_data)) {
        return null;
      }
      const articleRibbons = getArticleRibbons(this.article, articleExtraContent.ribbons_data);
      return articleRibbons;
    },
    disableAds() {
      return this.article.banners.disableAds;
    },
    disabledAdsList() {
      return this.$store.getters['adForm/getDisabledAdsList'];
    },
    adPlacements() {
      const placements: Record<string, any> = this.$store.getters['adForm/getAdsPlacements'];

      return placements || {};
    },
    contentAdPlacement() {
      return (this.adPlacements.content && this.adPlacements.content.mid[0]) || null;
    },
    contentFallbackAdPlacement() {
      return (this.adPlacements.mobilecrarticle && this.adPlacements.mobilecrarticle.mid[0]) || null;
    },
    contentFirstAdPlacement() {
      const placements: Record<string, any> = { desktop: null, mobile: null };

      if (this.adPlacements.contentfirst && this.adPlacements.contentfirst.mid.length && this.adPlacements.contentfirst.mid[0]) {
        placements.desktop = (this.adPlacements.contentfirst && this.adPlacements.contentfirst.mid[0]) || null;
      }

      if (this.adPlacements.mobilecontentfirst && this.adPlacements.mobilecontentfirst.mid.length && this.adPlacements.mobilecontentfirst.mid[0]) {
        placements.mobile = (this.adPlacements.mobilecontentfirst && this.adPlacements.mobilecontentfirst.mid[0]) || null;
      }

      return placements;
    },
    isMultimedijaArticle() {
      const { id } = this.$channelConfig('settings');

      return id === 'multimedija';
    },
  },
  mounted() {
    if (process.client) {
      const screenWidth = window.screen.width;
      const isIpad = (/Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1) as boolean;
      this.isMobileView = screenWidth <= gridConfig.breakpoints.lg || isIpad;
    } else {
      this.isMobileView = this.$ua && (this.$ua.isFromSmartphone() || this.$ua.isFromMobilephone() || this.$ua.isFromTablet());
    }

    this.$nextTick(() => {
      this.$emit('article-loaded', this.article.id);
    });
  },
  methods: {
    confirmAdult() {
      this.isAdultConfirmed = true;
    },
    handleSetFontSize() {
      this.fontSize = this.fontSize === 3 ? 1 : this.fontSize + 1;
    },
    getKey({ prefix, index }) {
      return getBannerKey({ prefix, route: this.$route, store: this.$store, index });
    },
  },
});
